// src/components/uniquepiece/UniquePieceBlock.tsx
import React, { Fragment } from "react";
import { UniquePieceItem } from "../../types/uniquepiece";

export default function UniquePieceBlock({ item }: { item: UniquePieceItem }) {
  /* Récupération des contenus existants */
  const paragraphs = [
    item.paragraphs1,
    item.paragraphs2,
    item.paragraphs3,
    item.paragraphs4,
  ].filter(Boolean) as string[];

  const images = [item.mainImage, ...(item.sideImages ?? [])];

  /* Construction des paires (image | texte) */
  const rawSections = Array.from(
    { length: Math.max(paragraphs.length, images.length) },
    (_, i) => ({
      text: paragraphs[i],
      image: images[i],
    })
  );

  /* Helper pour flush un groupe d’images seules */
  const renderImageCluster = (cluster: string[], key: string) => (
    <div
      key={key}
      className="flex flex-wrap justify-center gap-6 mb-10"
    >
      {cluster.map((src, i) => (
        <div
          key={i}
          className="w-full sm:w-[46%] lg:w-[30%] shrink-0 overflow-hidden rounded-lg"
        >
          <img
            src={src}
            alt={`${item.title}-${key}-${i}`}
            className="w-full h-auto md:h-[420px] object-contain md:object-cover"
            loading="lazy"
          />
        </div>
      ))}
    </div>
  );

  /* Parcours intelligent : on regroupe les images‑seules consécutives */
  const blocks: React.ReactNode[] = [];
  let buffer: string[] = [];

  rawSections.forEach((sec, idx) => {
    const isImgOnly = !!sec.image && !sec.text;

    if (isImgOnly) {
      buffer.push(sec.image!);
    }

    if (!isImgOnly || idx === rawSections.length - 1) {
      // S’il y a un buffer à flush avant le bloc textuel
      if (buffer.length) {
        blocks.push(renderImageCluster(buffer, `cluster-${idx}`));
        buffer = [];
      }
    }

    if (!isImgOnly) {
      blocks.push(
        <div
          key={`pair-${idx}`}
          className={`flex flex-col md:flex-row gap-8 mb-10 items-center font-semibold ${
            idx % 2 === 1 ? "md:flex-row-reverse" : ""
          }`}
        >
          {/* Image (si présente) */}
          {sec.image && (
            <div className="md:w-6/12 shrink-0">
              <div className="w-full overflow-hidden rounded-lg">
                <img
                  src={sec.image}
                  alt={`${item.title}-${idx}`}
                  className="w-full h-auto md:h-[420px] object-contain md:object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          )}

          {/* Texte (si présent) */}
          {sec.text && (
            <div
              className={`${
                sec.image ? "md:w-5/12" : "md:w-full"
              } prose prose-sm md:prose-base leading-relaxed`}
            >
              <p>{sec.text}</p>
            </div>
          )}
        </div>
      );
    }
  });

  return (
    <article className="mb-24">
      <h2 className="text-3xl font-bold mb-2">{item.title}</h2>
      {item.intro && <p className="text-gray-600 font-semibold mb-6 pl-0.5">{item.intro}</p>}

      {/* Rendu final */}
      {blocks}
    </article>
  );
}
